import { useCallback, useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import { useSnackbar } from "notistack";
import { ICategory, ITerm } from "@evennt-tech/api-v2-core";
import { useShallow } from "zustand/react/shallow";
import {
  ILibraryCategory,
  ILibrarySectionMatch,
  IPushShowInfoToLibraryReq,
  IShowInfoImportConflict,
  IShowInfoImportConflictRes,
  IShowInfoPushConflictRes,
  IShowInfoPushResolution,
  ITranslationConflict,
  ITranslationConflictResolution,
  ShowInfoImportAction,
  showInfoLibraryApi,
} from "api/showInfoLibrary";
import useOrganiser from "hooks/useOrganiser";
import useIsOrganiserCompanyAdmin from "hooks/useIsOrganiserCompanyAdmin";
import { useGlossaries } from "store/glossary/glossary.store";
import { useShowInfoLibrary } from "store/show-info-library/show-info-library.store";

/** AC 12's confirmation for `Add to Library`, shared by both push paths. */
const SECTION_ADDED_MESSAGE = "Section is added to the library";

type PushChoices = Pick<
  IPushShowInfoToLibraryReq,
  "resolution" | "categoryTranslations"
>;

type UseShowInfoLibraryActions = {
  /** True when this organiser may change library links at all. */
  isCompanyAdmin: boolean;
  /**
   * Whether `isCompanyAdmin` means anything yet. It is derived from the fetched
   * organiser, so it reads `false` until that lands — and a UI that locks on a
   * bare `false` locks itself for the first moment of every cold page.
   */
  isRoleResolved: boolean;
  /** The company library, for the `Add from Library` picker. */
  library: ILibraryCategory[];
  /** Re-reads the library, bypassing the store's loaded-once cache. */
  reloadLibrary: () => Promise<void>;
  isLoadingLibrary: boolean;

  /** Unresolved import collisions awaiting a `Duplicate warning` answer. */
  importConflicts: IShowInfoImportConflict[];
  /** Same-title library sections awaiting a push `Duplicate warning` answer. */
  pushMatches: ILibrarySectionMatch[] | null;
  /**
   * Category title translations that differ between this Show and the master
   * category the pushed section would fold into, awaiting a `Translation
   * conflict` answer.
   */
  translationConflicts: ITranslationConflict[] | null;
  /** The Show section currently being pushed, if any. */
  pushingTerm: ITerm | null;

  importSections: (sectionIds: string[]) => Promise<void>;
  replaceOnImport: () => Promise<void>;
  cancelImport: () => void;

  pushToLibrary: (term: ITerm) => Promise<void>;
  resolveTranslationConflicts: (
    resolutions: ITranslationConflictResolution[]
  ) => Promise<void>;
  resolvePush: (resolution: IShowInfoPushResolution) => Promise<void>;
  cancelPush: () => void;

  unlinkTerm: (term: ITerm) => Promise<void>;
  unlinkCategory: (category: ICategory) => Promise<void>;
};

const readConflicts = (error: unknown): IShowInfoImportConflict[] | null => {
  const { response } = error as AxiosError<IShowInfoImportConflictRes>;

  return response?.status === 409 && response.data?.conflicts
    ? response.data.conflicts
    : null;
};

const readPushMatches = (error: unknown): ILibrarySectionMatch[] | null => {
  const { response } = error as AxiosError<IShowInfoPushConflictRes>;

  return response?.status === 409 && response.data?.matches
    ? response.data.matches
    : null;
};

/**
 * Everything the Show-level Show Info page does with the library (FS-2890),
 * gathered in one place because the same operations are reached from three
 * different components — the page header's `+` menu, a section's edit form, and
 * a category's own menu — and each of them only wants to call a function.
 *
 * The collision flows deliberately mirror the server: an import or a push is
 * attempted, and a 409 is not an error but a **question**, held here until the
 * organiser answers it in a `Duplicate warning` and the call is retried with the
 * resolution attached. A push asks one more question up front: whether the
 * category's title translations clash with the master's, answered in a
 * `Translation conflict` before anything is written.
 */
const useShowInfoLibraryActions = (
  showId: string,
  /**
   * Whether this caller owns the library's live subscription. Exactly one should:
   * the store keys its listener by a single id, so a second subscriber adds
   * nothing while its unmount would tear the subscription down for the first —
   * and `GlossaryTerms` unmounts on its own whenever the Show has no categories
   * yet, which would silently kill the page's updates.
   *
   * The page passes `true`; components that only need the actions leave it off.
   */
  { subscribe = false }: { subscribe?: boolean } = {}
): UseShowInfoLibraryActions => {
  const [organiser] = useOrganiser();
  const isCompanyAdmin = useIsOrganiserCompanyAdmin();
  const { enqueueSnackbar } = useSnackbar();

  const organiserCompanyId =
    organiser?.organiserCompanyToOrganisers?.[0]?.organiserCompanyId;

  const { items, isLoading, fetch, autoCatch } = useShowInfoLibrary(
    useShallow((state) => ({
      items: state.items,
      isLoading: state.isLoading,
      fetch: state.fetch,
      autoCatch: state.autoCatch,
    }))
  );

  const { fetchCategories, fetchTerms } = useGlossaries(
    useShallow((state) => ({
      fetchCategories: state.fetchCategories,
      fetchTerms: state.fetchTerms,
    }))
  );

  const [importConflicts, setImportConflicts] = useState<
    IShowInfoImportConflict[]
  >([]);
  const [pendingSectionIds, setPendingSectionIds] = useState<string[]>([]);
  const [pushMatches, setPushMatches] = useState<ILibrarySectionMatch[] | null>(
    null
  );
  const [translationConflicts, setTranslationConflicts] = useState<
    ITranslationConflict[] | null
  >(null);
  const [categoryTranslations, setCategoryTranslations] = useState<
    ITranslationConflictResolution[] | undefined
  >();
  const [pushingTerm, setPushingTerm] = useState<ITerm | null>(null);

  useEffect(() => {
    if (!organiserCompanyId) return undefined;
    // Guarded inside the store, so several callers asking is one request.
    fetch(organiserCompanyId);

    return subscribe ? autoCatch() : undefined;
  }, [organiserCompanyId, subscribe]);

  /*
   * Opening the picker is the moment the list has to be current: the store keeps
   * a company's library once read, and anything created in another tab since
   * then would otherwise be missing.
   */
  const reloadLibrary = useCallback(async () => {
    if (!organiserCompanyId) return;

    await fetch(organiserCompanyId, true);
  }, [organiserCompanyId]);

  const library = useMemo(
    () => (organiserCompanyId ? items[organiserCompanyId] ?? [] : []),
    [items, organiserCompanyId]
  );

  /**
   * The acting client refreshes immediately rather than waiting for its own
   * notification to come back round — the server publishes the per-Show keys for
   * everyone else's benefit, not for the tab that made the change.
   */
  const refreshShow = useCallback(async () => {
    await fetchCategories(showId, true);
    await fetchTerms(showId, true);
  }, [showId, fetchCategories, fetchTerms]);

  const runImport = useCallback(
    async (sectionIds: string[], replace: boolean) => {
      if (!organiserCompanyId || !sectionIds.length) return;

      try {
        await showInfoLibraryApi.importFromLibrary({
          organiserCompanyId,
          showId,
          sectionIds,
          ...(replace && {
            resolutions: sectionIds.map((sectionId) => ({
              sectionId,
              action: ShowInfoImportAction.REPLACE,
            })),
          }),
        });

        setImportConflicts([]);
        setPendingSectionIds([]);
        await refreshShow();
      } catch (error) {
        const conflicts = readConflicts(error);

        if (conflicts) {
          setPendingSectionIds(sectionIds);
          setImportConflicts(conflicts);
          return;
        }

        console.error("Error occurred adding show info from library", error);
        throw error;
      }
    },
    [organiserCompanyId, showId, refreshShow]
  );

  const importSections = useCallback(
    async (sectionIds: string[]) => runImport(sectionIds, false),
    [runImport]
  );

  const replaceOnImport = useCallback(async () => {
    /*
     * Only the colliding sections are re-sent with `REPLACE`; the rest of the
     * original selection went in on the first, rejected call and is re-sent here
     * too, since that call rolled back whole. Sending the full pending set keeps
     * "one import, one transaction" true.
     */
    await runImport(pendingSectionIds, true);
  }, [pendingSectionIds, runImport]);

  const cancelImport = useCallback(() => {
    setImportConflicts([]);
    setPendingSectionIds([]);
  }, []);

  const cancelPush = useCallback(() => {
    setPushingTerm(null);
    setPushMatches(null);
    setTranslationConflicts(null);
    setCategoryTranslations(undefined);
  }, []);

  const runPush = useCallback(
    async (term: ITerm, choices: PushChoices) => {
      if (!organiserCompanyId) return;

      try {
        await showInfoLibraryApi.pushToLibrary({
          organiserCompanyId,
          showId,
          termId: term.id,
          ...choices,
        });

        cancelPush();
        // The section looks identical afterwards apart from a small badge, so
        // without this the organiser has no signal the push landed at all.
        enqueueSnackbar(SECTION_ADDED_MESSAGE, { variant: "success" });
        await refreshShow();
      } catch (error) {
        const matches = readPushMatches(error);

        if (matches) {
          setPushingTerm(term);
          setPushMatches(matches);
          return;
        }

        console.error("Error occurred adding show info to library", error);
        throw error;
      }
    },
    [organiserCompanyId, showId, refreshShow, enqueueSnackbar, cancelPush]
  );

  /**
   * The translation check runs before the write, as the Build Type flow does.
   * A same-title 409 raised by the push that follows then retries with the
   * answered `categoryTranslations` still attached, so the organiser is asked
   * each question once.
   */
  const pushToLibrary = useCallback(
    async (term: ITerm) => {
      if (!organiserCompanyId) return;

      let conflicts: ITranslationConflict[];
      try {
        conflicts = await showInfoLibraryApi.getTranslationConflicts({
          organiserCompanyId,
          showId,
          termId: term.id,
        });
      } catch (error) {
        console.error(
          "Error occurred checking show info translation conflicts",
          error
        );
        throw error;
      }

      if (conflicts.length) {
        setPushingTerm(term);
        setTranslationConflicts(conflicts);
        return;
      }

      await runPush(term, {});
    },
    [organiserCompanyId, showId, runPush]
  );

  const resolveTranslationConflicts = useCallback(
    async (resolutions: ITranslationConflictResolution[]) => {
      if (!pushingTerm) return;

      setTranslationConflicts(null);
      setCategoryTranslations(resolutions);
      await runPush(pushingTerm, { categoryTranslations: resolutions });
    },
    [pushingTerm, runPush]
  );

  const resolvePush = useCallback(
    async (resolution: IShowInfoPushResolution) => {
      if (!pushingTerm) return;

      await runPush(pushingTerm, { resolution, categoryTranslations });
    },
    [pushingTerm, categoryTranslations, runPush]
  );

  const unlinkTerm = useCallback(
    async (term: ITerm) => {
      if (!organiserCompanyId) return;

      try {
        await showInfoLibraryApi.unlinkFromLibrary({
          organiserCompanyId,
          showId,
          termId: term.id,
        });
        await refreshShow();
      } catch (error) {
        console.error("Error occurred unlinking show info section", error);
      }
    },
    [organiserCompanyId, showId, refreshShow]
  );

  const unlinkCategory = useCallback(
    async (category: ICategory) => {
      if (!organiserCompanyId) return;

      try {
        await showInfoLibraryApi.unlinkFromLibrary({
          organiserCompanyId,
          showId,
          categoryId: category.id,
        });
        await refreshShow();
      } catch (error) {
        console.error("Error occurred unlinking show info category", error);
      }
    },
    [organiserCompanyId, showId, refreshShow]
  );

  return {
    isCompanyAdmin,
    isRoleResolved: !!organiser,
    library,
    reloadLibrary,
    isLoadingLibrary: isLoading,
    importConflicts,
    pushMatches,
    translationConflicts,
    pushingTerm,
    importSections,
    replaceOnImport,
    cancelImport,
    pushToLibrary,
    resolveTranslationConflicts,
    resolvePush,
    cancelPush,
    unlinkTerm,
    unlinkCategory,
  };
};

export default useShowInfoLibraryActions;
